import { Serializable } from '../../common/types'
import { FlytrexMapDrawable } from 'src/app/controls/flytrex-map-drawable.interface'
import { uniqueId } from 'src/app/common/unique-id'
import { FlightNetworkNodeState } from 'src/app/@store'

export enum NetworkNodeType {
  Center = 'center',
  Delivery = 'delivery',
  Semaphore = 'semaphore',
  Waypoint = 'waypoint',
  Drone = 'drone-',
  WaitingDelivery = 'waitingDelivery',
}

export enum MissionStateIndication {
  InRoute = 'in_route',
  HeadingBack = 'heading_back',
  Failure = 'failure',
  Unknown = 'unknown',
}

interface NetworkNodesWithLabels extends ReadonlyArray<{ label: string; value: NetworkNodeType }> {}
export const EditableNetworkNodeTypeArray: NetworkNodesWithLabels = [
  { value: NetworkNodeType.Delivery, label: 'Delivery' },
  { value: NetworkNodeType.Semaphore, label: 'Semaphore' },
  { value: NetworkNodeType.Waypoint, label: 'Waypoint' },
] as const

interface TestPointLabelsAndValues extends ReadonlyArray<{ label: string; value: boolean }> {}
export const TestPointValues: TestPointLabelsAndValues = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
] as const

export class NetworkNode extends Serializable<NetworkNode> implements FlightNetworkNodeState, FlytrexMapDrawable {
  id: number | null = null
  name: string | null = null
  flight_altitude_m: number | null = null
  ground_elevation_m: number | null = null
  latitude_deg: number | null = null
  longitude_deg: number | null = null
  network_id: number | null = null
  node_type: NetworkNodeType | null = null
  uuid: string | null = null
  is_test_point: boolean | null = null
  point_type?: NetworkNodeType | null = null

  saved_flight_altitude?: number | null
  saved_ground_elevation?: number | null

  idForMap: string

  constructor(details: Partial<NetworkNode>) {
    super()
    this.checkAndDeserialize(details)
  }

  deserialize(details: Partial<NetworkNode>): void {
    this.id = details.id
    this.name = details.name ?? ''
    this.flight_altitude_m = details.flight_altitude_m
    this.ground_elevation_m = details.ground_elevation_m
    this.latitude_deg = Number(details.latitude_deg)
    this.longitude_deg = Number(details.longitude_deg)
    this.network_id = details.network_id
    this.node_type = details.node_type ?? null
    this.idForMap = this.idForMap ?? details.idForMap ?? this.getIdForMap()
    this.uuid = details.uuid
    this.is_test_point = details.is_test_point
  }

  getLatLng(): google.maps.LatLng {
    return new google.maps.LatLng(this.latitude_deg, this.longitude_deg)
  }

  equals(node: NetworkNode): boolean {
    const coordsAreEqual = this.getLatLng().equals(node.getLatLng())
    const flightAltIsEqual = this.flight_altitude_m === node.flight_altitude_m
    const groundAltIsEqual = this.ground_elevation_m === node.ground_elevation_m
    return coordsAreEqual && flightAltIsEqual && groundAltIsEqual
  }

  getIdForMap(): string {
    if (!this.idForMap) {
      this.idForMap = `node${uniqueId()}`
    }
    return this.idForMap
  }

  toJSON(): FlightNetworkNodeState {
    return {
      id: this.id,
      name: this.name,
      flight_altitude_m: this.flight_altitude_m,
      ground_elevation_m: this.ground_elevation_m,
      latitude_deg: this.latitude_deg,
      longitude_deg: this.longitude_deg,
      network_id: this.network_id,
      node_type: this.node_type,
      uuid: this.uuid,
      is_test_point: this.is_test_point || false,
    }
  }
}
